import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import useStore from "@/services/store";
import api from "@/services/api";

import UplodFileSVG from "@/assets/svg/upload-file.svg";
import Loader from "@/components/Loader";
import DocumentTextIcon from "@heroicons/react/24/outline/DocumentTextIcon";
import EyeIcon from "@heroicons/react/24/outline/EyeIcon";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import { HiInformationCircle } from "react-icons/hi2";

const Dataroom = () => {
  const navigate = useNavigate();
  const { user } = useStore();
  const [loading, setLoading] = useState(false);
  const [annonces, setAnnonces] = useState([]); // The annonces where the user is the owner
  const [annoncesWithNDASignature, setAnnoncesWithNDASignature] = useState([]); // The annonces where the user has signed the NDA

  const fetchAnnonces = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/company/search", { user_id: user._id, annonce: true, annonce_status: "ACCEPTED", annonce_type: "cession" });
      if (!ok) return toast.error("Erreur lors de la récupération de vos annonces de cession");
      setAnnonces(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la récupération de vos annonces de cession");
    }
  };

  const fetchAnnoncesWithNDASignature = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/company/search", {
        annonce: true,
        annonce_status: "ACCEPTED",
        annonce_type: "cession",
        nda: true,
      });
      if (!ok) return toast.error("Erreur lors de la récupération des annonces dont le NDA a été signé");
      setAnnoncesWithNDASignature(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la récupération des annonces dont le NDA a été signé");
    }
  };

  useEffect(() => {
    fetchAnnonces();
  }, []);

  useEffect(() => {
    fetchAnnoncesWithNDASignature();
  }, []);

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 lg:p-8">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Data room</h1>
          <p className="text-lg text-gray-600 mb-6">Gérez l'accès aux documents de vos annonces</p>
        </header>

        {/* Access/edit documents of annonce owned by the user */}
        {annonces.length > 0 && <h3 className="text-lg font-semibold mb-4 mt-8">Vos annonces de cession</h3>}
        <div className="flex items-center bg-blue-50 border border-blue-200 px-4 py-3 rounded-xl my-2">
          <HiInformationCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" />
          <span className="text-sm text-blue-800 font-medium">Une fois le NDA signé par le(s) demandeur(s), vos documents seront automatiquement partagés</span>
        </div>
        <div className="space-y-8">
          {annonces.map((annonce) => (
            <div key={annonce._id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                <div className="lg:col-span-1 p-6 lg:p-8 border-r border-gray-100">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0">
                      {annonce.annonce_image ? (
                        <img src={annonce.annonce_image} alt="Annonce" className="h-20 w-20 rounded-xl object-cover border border-gray-200 shadow-sm" />
                      ) : (
                        <div className="h-20 w-20 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl text-blue-600 text-2xl border border-blue-200">
                          <span>📄</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 mb-1 truncate">{annonce.annonce_title}</h3>
                      <p className="text-sm text-gray-500 mb-3">
                        {annonce.annonce_created_at ? `Créée le ${new Date(annonce.annonce_created_at).toLocaleDateString("fr-FR")}` : "Date inconnue"}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{annonce.annonce_type}</span>
                        {annonce.annonce_selling_price && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{annonce.annonce_selling_price} €</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-6 max-h-max">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {annonce.annonce_description || <span className="italic text-gray-400">Aucune description disponible.</span>}
                    </p>
                  </div>

                  <div className="flex justify-center">
                    <button
                      className="inline-flex items-center px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm hover:shadow-md"
                      onClick={() => navigate(`/annonce/${annonce._id}/edit`)}>
                      Éditer l'annonce
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-2 p-6 lg:p-8 bg-gray-50">
                  <Documents company={annonce} isOwner={true} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Access documents after NDA signature */}
        {annoncesWithNDASignature.length > 0 && <h3 className="text-lg font-semibold mb-4 mt-8">Annonces où vous avez signé le NDA</h3>}
        <div className="space-y-8">
          {annoncesWithNDASignature.map((annonce) => (
            <div key={annonce._id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                <div className="lg:col-span-1 p-6 lg:p-8 border-r border-gray-100">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0">
                      {annonce.annonce_image ? (
                        <img src={annonce.annonce_image} alt="Annonce" className="h-20 w-20 rounded-xl object-cover border border-gray-200 shadow-sm" />
                      ) : (
                        <div className="h-20 w-20 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl text-blue-600 text-2xl border border-blue-200">
                          <span>📄</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 mb-1 truncate">{annonce.annonce_title}</h3>
                      <p className="text-sm text-gray-500 mb-3">
                        {annonce.annonce_created_at ? `Créée le ${new Date(annonce.annonce_created_at).toLocaleDateString("fr-FR")}` : "Date inconnue"}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{annonce.annonce_type}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
                    <p className="text-gray-600 text-sm leading-relaxed max-h-[100px] overflow-y-auto">{annonce.annonce_description}</p>
                  </div>

                  <div className="flex justify-center">
                    <button
                      className="inline-flex items-center px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm hover:shadow-md"
                      onClick={() => navigate(`/annonce/${annonce._id}`)}>
                      Voir l'annonce
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-2 p-6 lg:p-8 bg-gray-50">
                  <Documents company={annonce} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Documents = ({ company, isOwner = false }) => {
  if (!company || !company._id) return null;

  const { user } = useStore();
  const [documents, setDocuments] = useState([]);
  const [nda, setNDA] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const { ok, data } = await api.post("/document/search", { company_id: company._id });
      if (!ok) return toast.error(`Une erreur est survenue lors de la récupération des documents de l'annonce: ${company.annonce_title}`);
      setDocuments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue");
      setDocuments([]);
    }
    setLoading(false);
  };

  const fetchNDA = async () => {
    setLoading(true);
    try {
      const { ok, data } = await api.post("/nda/search", { company_id: company._id, user_id: user._id });
      if (!ok) return toast.error(`Une erreur est survenue lors de la récupération du NDA de l'annonce: ${company.annonce_title}`);
      const ndaData = Array.isArray(data) && data.length > 0 ? data[0] : null; // NDA search returns an array, we want the first (and should be only) NDA for this company
      setNDA(ndaData);
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue");
      setNDA(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchNDA();
  }, [company._id]);

  const handleFileUpload = async (e) => {
    try {
      setLoading(true);
      const formData = new FormData();
      for (let i = 0; i < e.target.files.length; i++) {
        formData.append("files", e.target.files[i]);
      }
      const { data } = await api.postFormData(`/document?company_id=${company._id}`, formData);
      setDocuments(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue");
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  // Show pending message if NDA status is PENDING and user is not the owner
  if (!isOwner && nda && nda.status !== "ACCEPTED") {
    return (
      <div className="h-full">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900">Documents partagés</h3>
          {nda?.nda_pdf_url && (
            <a
              href={nda.nda_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-600 text-blue-600 text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors shadow-sm">
              <DocumentTextIcon className="w-4 h-4 text-blue-600" />
              <span>Voir le NDA signé</span>
            </a>
          )}
        </div>
        <p className="text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          Votre demande est étudiée par notre équipe, nous faisons au plus vite pour vous donner l'accès aux documents
        </p>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900">Documents partagés</h3>
        <div className="flex items-center gap-3">
          {nda?.nda_pdf_url && (
            <a
              href={nda.nda_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-600 text-blue-600 text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors shadow-sm">
              <DocumentTextIcon className="w-4 h-4 text-blue-600" />
              <span>Voir le NDA signé</span>
            </a>
          )}
          <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-200">
            {documents?.length || 0} document{(documents?.length || 0) !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {(documents?.length || 0) > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-sm font-semibold text-gray-700">
              <div className="col-span-6">Nom du fichier</div>
              <div className="col-span-3">Date d'ajout</div>
              <div className="col-span-2 text-center">Voir</div>
              {isOwner && <div className="col-span-1 text-center">Actions</div>}
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {documents.map((item, index) => (
              <DocRow key={index} item={item} index={index} setDocuments={setDocuments} isOwner={isOwner} />
            ))}
          </div>
        </div>
      )}

      {isOwner && (
        <div className="flex justify-center">
          <label
            htmlFor={`documents-${company._id}`}
            className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-8 w-full hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 cursor-pointer flex flex-col items-center justify-center group">
            {loading ? (
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-blue-600 animate-bounce" />
                <p className="text-blue-600 font-medium">Chargement...</p>
              </div>
            ) : (
              <>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                  <img src={UplodFileSVG} alt="upload" className="w-6 h-6" />
                </div>
                <p className="text-gray-700 font-medium mb-2">Cliquez pour déposer des fichiers</p>
                <p className="text-sm text-gray-500 text-center">Glissez-déposez vos documents ou cliquez pour parcourir</p>
              </>
            )}
          </label>
          <input id={`documents-${company._id}`} type="file" className="hidden" disabled={loading} multiple onChange={handleFileUpload} />
        </div>
      )}
    </div>
  );
};

const DocRow = ({ item, index, setDocuments, isOwner = false }) => {
  const [file_name, setFileName] = useState(item.file_name);

  useEffect(() => {
    setFileName(item.file_name);
  }, [item]);

  useEffect(() => {
    if (file_name === item.file_name) return;

    const timeout = setTimeout(async () => {
      const res = await api.put(`/document/${item._id}`, { file_name });
      if (res.ok) {
        setDocuments((prev) => {
          const newValue = [...prev];
          newValue[index].file_name = file_name;
          return newValue;
        });
        toast.success("Nom du fichier mis à jour");
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [file_name]);

  const handleDeleteFile = async () => {
    const confirm = window.confirm("Voulez-vous vraiment supprimer ce fichier ?");
    if (!confirm) return;
    try {
      const res = await api.delete(`/document/${item._id}`);
      if (res.ok) setDocuments((prev) => prev.filter((doc) => doc._id !== item._id));
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-gray-50 transition-colors">
      <div className="col-span-6 flex items-center">
        <DocumentTextIcon className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" />
        <input
          type="text"
          value={file_name}
          className="flex-1 bg-transparent border-none text-gray-900 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-2 py-1"
          onChange={(e) => setFileName(e.target.value)}
        />
      </div>
      <div className="col-span-3 text-sm text-gray-600">{new Date(item.created_at).toLocaleDateString("fr-FR")}</div>
      <div className="col-span-2 flex justify-center">
        <a
          href={item.file_url && item.file_url.startsWith("http") ? item.file_url : `https://${item.file_url}`}
          target="_blank"
          rel="noreferrer"
          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
          <EyeIcon className="w-5 h-5" />
        </a>
      </div>
      {isOwner && (
        <div className="col-span-1 flex justify-center">
          <button type="button" className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" onClick={handleDeleteFile}>
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Dataroom;
